import { useEffect, useState, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Global axios request interceptor.
//
// WHY: `capacitor.config.json` enables `CapacitorHttp` for iOS/Android
// builds, which intercepts every fetch/XHR call and routes it through
// native networking.  When the request body is a JavaScript object,
// CapacitorHttp does NOT auto-set `Content-Type: application/json`, so
// FastAPI/Pydantic parses the body as form-data and returns 422.  The
// LikeButton / follow / bookmark / diversified-POST call all silently
// failed on native for exactly this reason — the request went out, came
// back a 4xx, the frontend rolled back the optimistic update, and the
// user saw "the button doesn't work".
//
// Setting the headers globally means every existing AND future axios
// call in the app gets correct JSON semantics on native, with zero
// per-call boilerplate.  The interceptor only touches the headers — it
// never modifies the URL, body, method, or timeout.
// ---------------------------------------------------------------------------
axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  const hasBody = ['post', 'put', 'patch', 'delete'].includes(method);
  // Only tag body-carrying requests as JSON — GETs don't need a
  // Content-Type header and some proxies (e.g. cloudfront) reject one.
  if (hasBody) {
    config.headers = config.headers || {};
    if (!config.headers['Content-Type'] && !config.headers['content-type']) {
      config.headers['Content-Type'] = 'application/json';
    }
  }
  if (!config.headers?.Accept && !config.headers?.accept) {
    config.headers = config.headers || {};
    config.headers.Accept = 'application/json';
  }
  return config;
});

// ---------------------------------------------------------------------------
// Global axios RESPONSE interceptor — silent 401 handling.
//
// Before this: every stale JWT caused each authenticated call to fail with
// 401.  Components (LikeButton, admin dashboard, follow, etc.) each ran
// their own error UI — LikeButton toasted "Session expired" and hard-
// redirected to /auth?reason=session_expired, forcing users off the page
// they were reading.  On iOS/Android that felt like the app was kicking
// them out.
//
// Now: on ANY 401, we silently drop the stale credentials and let the
// request return the error to the caller.  Most surfaces work fine
// anonymously (feed, discover, profiles, content detail, share), and the
// interactions that DO require auth (like, follow, comment, bookmark)
// can quietly no-op — the user just doesn't see a scary bounce.  If the
// user actively navigates to /create, /settings, etc., normal route
// guards will prompt them to sign in.
//
// We intentionally DO NOT force a redirect and DO NOT emit a toast.
// ---------------------------------------------------------------------------
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      try {
        // Clear the stale credentials so subsequent calls don't loop the
        // same 401.  We keep the cached user object for a moment so the UI
        // doesn't visibly re-render as "signed out" mid-page — the next
        // navigation naturally re-reads localStorage and updates.
        window.localStorage.removeItem('zenscroll_token');
      } catch { /* private mode / storage disabled */ }
    }
    return Promise.reject(error);
  },
);

// -----------------------------------------------------------------------
// Eager imports — pages needed on the initial paint / auth shell.
//   * Splash — the first frame shown while token check happens.
//   * Welcome — anonymous landing; also the entrypoint for the native
//     pre-login screen (via internal branch).
//   * Auth + Apple* — sign-in flows; users often deep-link to /auth.
//   * ErrorBoundary + shell UI — must always be resolved synchronously.
// Everything else is `React.lazy()` below so it downloads on demand.
// -----------------------------------------------------------------------
import Splash from './pages/Splash';
import Welcome from './pages/Welcome';
import Auth from './pages/Auth';
import AppleAuth from './pages/AppleAuth';
import AppleCallback from './pages/AppleCallback';
import AppleSuccess from './pages/AppleSuccess';
import HeardAboutSurvey from './components/HeardAboutSurvey';
import RatingSurveyModal from './components/RatingSurveyModal';
import ErrorBoundary from './components/ErrorBoundary';
import AdminErrorBoundary from './components/admin/AdminErrorBoundary';
import MoodExitModal from './components/MoodExitModal';
import OnboardingTutorial from './components/OnboardingTutorial';
import MobileBottomNav from './components/MobileBottomNav';
import AndroidScrollDiagnostic from './components/AndroidScrollDiagnostic';
import RouteSuspenseFallback from './components/RouteSuspenseFallback';
import { BUILD_ID } from './constants/build';
import { useMobileChromeAutoHide } from './hooks/useMobileChromeAutoHide';
import ChromeDebugOverlay from './components/ChromeDebugOverlay';

// -----------------------------------------------------------------------
// Lazy-loaded routes — code-split per route so the initial bundle only
// ships the marketing shell + auth.  Chunk names use `webpackChunkName`
// magic comments so we get meaningful filenames in the build output.
//
// !! ASSET URLS MUST BE ROOT-BASED — `"homepage": "/"` in package.json !!
// CRA derives the chunk base URL from `homepage`.  With `"./"` it emits
// RELATIVE chunk URLs, which the Capacitor WebView resolves against the
// CURRENT ROUTE — e.g. on /profile it requested
//   capacitor://localhost/profile/static/js/172.xxxx.chunk.js  (404)
// instead of
//   capacitor://localhost/static/js/172.xxxx.chunk.js
// producing a ChunkLoadError and the full-screen error fallback on native
// iOS for every lazy route opened from a nested path.  Keep it "/".
//
// `lazyPage` wraps React.lazy with a targeted [CHUNK ERROR] log (route,
// chunk URL from the error message, build, platform) so any future chunk
// resolution failure is instantly identifiable in Xcode / logcat.  The
// error is re-thrown so the ErrorBoundary still shows its fallback.
// -----------------------------------------------------------------------
const lazyPage = (importer) =>
  lazy(() =>
    importer().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('[CHUNK ERROR] lazy route chunk failed to load:', {
        route: (typeof window !== 'undefined' && window.location?.pathname) || 'unknown',
        platform: (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web',
        build: BUILD_ID,
        name: error?.name,
        // ChunkLoadError messages include the requested chunk URL.
        message: error?.message,
      });
      throw error;
    })
  );
const Home             = lazyPage(() => import(/* webpackChunkName: "page-home" */ './pages/Home'));
const Create           = lazyPage(() => import(/* webpackChunkName: "page-create" */ './pages/Create'));
const Discover         = lazyPage(() => import(/* webpackChunkName: "page-discover" */ './pages/Discover'));
const Profile          = lazyPage(() => import(/* webpackChunkName: "page-profile" */ './pages/ProfileNew'));
const Prompts          = lazyPage(() => import(/* webpackChunkName: "page-prompts" */ './pages/Prompts'));
const Onboarding       = lazyPage(() => import(/* webpackChunkName: "page-onboarding" */ './pages/Onboarding'));
const ContentDetail    = lazyPage(() => import(/* webpackChunkName: "page-content-detail" */ './pages/ContentDetail'));
const Zenflow         = lazyPage(() => import(/* webpackChunkName: "page-zenflow" */ './pages/Zenflow'));
const Play            = lazyPage(() => import(/* webpackChunkName: "page-play" */ './pages/Play'));
const PlayCategory    = lazyPage(() => import(/* webpackChunkName: "page-play-cat" */ './pages/PlayCategory'));
const Following        = lazyPage(() => import(/* webpackChunkName: "page-following" */ './pages/Following'));
const TopPicks         = lazyPage(() => import(/* webpackChunkName: "page-top-picks" */ './pages/TopPicks'));
const Settings         = lazyPage(() => import(/* webpackChunkName: "page-settings" */ './pages/Settings'));
const Notifications    = lazyPage(() => import(/* webpackChunkName: "page-notifications" */ './pages/Notifications'));
const About            = lazyPage(() => import(/* webpackChunkName: "page-about" */ './pages/About'));
const ResetPassword    = lazyPage(() => import(/* webpackChunkName: "page-reset-password" */ './pages/ResetPassword'));
const Help             = lazyPage(() => import(/* webpackChunkName: "page-help" */ './pages/Help'));
const Privacy          = lazyPage(() => import(/* webpackChunkName: "page-privacy" */ './pages/Privacy'));
const Terms            = lazyPage(() => import(/* webpackChunkName: "page-terms" */ './pages/Terms'));
const DeleteAccount    = lazyPage(() => import(/* webpackChunkName: "page-delete-account" */ './pages/DeleteAccount'));
const ChildSafety      = lazyPage(() => import(/* webpackChunkName: "page-child-safety" */ './pages/ChildSafety'));
const ComingSoon       = lazyPage(() => import(/* webpackChunkName: "page-coming-soon" */ './pages/ComingSoon'));
// Admin pages live in their own chunks so anonymous / regular users
// never download the admin bundle.
const AdminDashboard      = lazyPage(() => import(/* webpackChunkName: "page-admin-dashboard" */ './pages/AdminDashboard'));
const AdminAds            = lazyPage(() => import(/* webpackChunkName: "page-admin-ads" */ './pages/AdminAds'));
const AdminModeration     = lazyPage(() => import(/* webpackChunkName: "page-admin-moderation" */ './pages/AdminModeration'));
const AdminAISettings     = lazyPage(() => import(/* webpackChunkName: "page-admin-ai" */ './pages/AdminAISettings'));
const AdminContentLibrary = lazyPage(() => import(/* webpackChunkName: "page-admin-content-lib" */ './pages/AdminContentLibrary'));
const AdminRatings     = lazyPage(() => import(/* webpackChunkName: "page-admin-ratings" */ './pages/AdminRatings'));
const AdminContentHealth = lazyPage(() => import(/* webpackChunkName: "page-admin-health" */ './pages/AdminContentHealth'));
const AdminAcquisition    = lazyPage(() => import(/* webpackChunkName: "page-admin-acquisition" */ './pages/AdminAcquisition'));
const AdminFreshDrops     = lazyPage(() => import(/* webpackChunkName: "page-admin-fresh-drops" */ './pages/AdminFreshDrops'));
const AdminCaptions       = lazyPage(() => import(/* webpackChunkName: "page-admin-captions" */ './pages/AdminCaptions'));
const AdminChallengeBlocklist = lazyPage(() => import(/* webpackChunkName: "page-admin-challenge-blocklist" */ './pages/AdminChallengeArtworkBlocklist'));
const FreshDrop           = lazyPage(() => import(/* webpackChunkName: "page-fresh-drop" */ './pages/FreshDrop'));

// `/profile/me` → real `/profile/:username` redirect.  Reads the cached
// user first (no network) and falls back to a one-shot `/api/profile/me`
// fetch when the cache is empty (e.g. immediately after sign-in).
// Lifted to module scope so it isn't redefined on every AppRouter render —
// the in-render definition caused a new component identity on every
// navigation, which dropped its internal `target` state.
function MeRedirect({ token }) {
  const [target, setTarget] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('zenscroll_user') || 'null');
      return u?.username ? `/profile/${u.username}` : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (target) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${BACKEND_URL}/api/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.data) {
          try { localStorage.setItem('zenscroll_user', JSON.stringify(r.data)); } catch { /* private mode */ }
          setTarget(r.data.username ? `/profile/${r.data.username}` : '/');
        } else {
          setTarget('/');
        }
      })
      .catch(() => setTarget('/'));
  }, [target, token]);
  if (!target) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#0A1514] text-[#94A3B8] text-sm">
        Loading profile…
      </div>
    );
  }
  return <Navigate to={target} replace />;
}

function AppRouter({ token, setToken, hasOnboarded, setHasOnboarded, handleLogin, handleLogout }) {
  const location = useLocation();
  // Wire the mobile chrome auto-hide listener.  The hook itself reads the
  // current pathname to decide whether to enable hiding (disabled on
  // /auth, /onboarding, /settings, and on tablet/desktop).
  useMobileChromeAutoHide();
  
  if (location.pathname === '/auth/apple/callback') {
    return <AppleCallback onLogin={handleLogin} />;
  }
  
  if (location.pathname === '/auth/apple/success') {
    return <AppleSuccess onLogin={handleLogin} />;
  }

  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
    <Routes>
      <Route path="/" element={<Welcome token={token} />} />
      <Route path="/welcome" element={<Welcome token={token} />} />
      <Route path="/home" element={<Welcome token={token} />} />
      <Route path="/zenflow" element={<Zenflow token={token} />} />
      <Route path="/play" element={<Play token={token} />} />
      <Route path="/play/:category" element={<PlayCategory token={token} />} />
      <Route path="/top-picks" element={<TopPicks token={token} />} />
      <Route path="/following" element={token ? <Following token={token} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/connect" element={token ? <Discover token={token} onLogout={handleLogout} /> : <Navigate to="/" />} />
      <Route path="/help" element={<Help token={token} />} />
      <Route path="/explore" element={<Navigate to="/zenflow" replace />} />
      <Route path="/prompts" element={<Prompts token={token} />} />
      <Route path="/trending" element={<Prompts token={token} />} />
      <Route path="/splash" element={!token ? <Splash /> : <Navigate to="/" />} />
      <Route
        path="/auth"
        element={!token ? <Auth onLogin={handleLogin} /> : <Navigate to="/zenflow" />}
      />
      <Route
        path="/auth/apple"
        element={!token ? <AppleAuth onLogin={handleLogin} /> : <Navigate to="/zenflow" />}
      />
      <Route
        path="/onboarding"
        element={
          token && !hasOnboarded ? (
            <Onboarding token={token} onComplete={() => setHasOnboarded(true)} />
          ) : (
            <Navigate to={token ? '/home' : '/'} />
          )
        }
      />
      <Route
        path="/feed"
        element={
          token ? (
            hasOnboarded ? (
              <Home token={token} onLogout={handleLogout} />
            ) : (
              <Navigate to="/onboarding" />
            )
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/create"
        element={token ? <Create token={token} onLogout={handleLogout} /> : <Navigate to="/" />}
      />
      <Route
        path="/discover"
        element={token ? <Discover token={token} onLogout={handleLogout} /> : <Navigate to="/" />}
      />
      <Route
        path="/profile/me"
        element={token ? <MeRedirect token={token} /> : <Navigate to="/" />}
      />
      <Route
        path="/profile/:username"
        element={token ? <Profile token={token} onLogout={handleLogout} /> : <Navigate to="/" />}
      />
      <Route
        path="/profile"
        element={token ? <Profile token={token} onLogout={handleLogout} /> : <Navigate to="/" />}
      />
      <Route
        path="/content/:contentId"
        element={<ContentDetail token={token} onLogout={handleLogout} />}
      />
      <Route
        path="/admin"
        element={token ? <AdminErrorBoundary><AdminDashboard token={token} onLogout={handleLogout} /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/ads"
        element={token ? <AdminErrorBoundary><AdminAds token={token} /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/moderation"
        element={token ? <AdminErrorBoundary><AdminModeration token={token} /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/ai-settings"
        element={token ? <AdminErrorBoundary><AdminAISettings token={token} /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/content-library"
        element={token ? <AdminErrorBoundary><AdminContentLibrary /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/ratings"
        element={token ? <AdminErrorBoundary><AdminRatings /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/content-health"
        element={token ? <AdminErrorBoundary><AdminContentHealth /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/acquisition"
        element={token ? <AdminErrorBoundary><AdminAcquisition /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/fresh-drops"
        element={token ? <AdminErrorBoundary><AdminFreshDrops /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/captions"
        element={token ? <AdminErrorBoundary><AdminCaptions /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/admin/challenges/blocklist"
        element={token ? <AdminErrorBoundary><AdminChallengeBlocklist /></AdminErrorBoundary> : <Navigate to="/" />}
      />
      <Route
        path="/fresh-drop"
        element={token ? <FreshDrop token={token} /> : <Navigate to="/auth?next=/fresh-drop" />}
      />
      <Route
        path="/settings"
        element={token ? (
          <ErrorBoundary>
            <Settings token={token} onLogout={handleLogout} />
          </ErrorBoundary>
        ) : <Navigate to="/" />}
      />
      <Route
        path="/notifications"
        element={token ? <Notifications token={token} /> : <Navigate to="/" />}
      />
      <Route path="/about" element={<About token={token} />} />
      <Route path="/privacy" element={<Privacy token={token} />} />
      <Route path="/terms" element={<Terms token={token} />} />
      <Route path="/delete-account" element={<DeleteAccount token={token} />} />
      <Route path="/child-safety" element={<ChildSafety token={token} />} />
      <Route path="/coming-soon" element={<ComingSoon token={token} />} />
      <Route path="/reset-password" element={<ResetPassword />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('zenscroll_token'));
  const [hasOnboarded, setHasOnboarded] = useState(
    localStorage.getItem('zenscroll_onboarded') === 'true'
  );

  // Build stamp — printed on every app boot and pinned to <html> so QA can
  // grep for it via remote-debug to confirm the device is running the
  // freshly-synced Capacitor bundle (and not a stale APK).
  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-build-id', BUILD_ID);
      // Global handle for `chrome://inspect`, Capacitor remote, etc.
      window.__ZENSCROLL_BUILD_ID = BUILD_ID;
       
      console.log(`%c[ZenScroll] BUILD_ID=${BUILD_ID}`, 'background:#1C4749;color:#FFD166;padding:2px 6px;border-radius:4px;font-weight:600');
    } catch { /* SSR / locked DOM — silently ignore */ }
  }, []);

  useEffect(() => {
    if (token) {
      localStorage.setItem('zenscroll_token', token);
      // Cache the user profile (email + username + avatar) so things like the
      // admin gate and avatar can read it synchronously from localStorage.
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      axios
        .get(`${BACKEND_URL}/api/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (res.data) localStorage.setItem('zenscroll_user', JSON.stringify(res.data));
        })
        .catch(() => { /* non-blocking */ });
    } else {
      localStorage.removeItem('zenscroll_token');
      localStorage.removeItem('zenscroll_user');
    }
  }, [token]);

  // Global broken-content guard.
  // Listens for `error` events bubbling up from any <img> or <video> and hides
  // the closest card-like ancestor.  This way a 404'd asset never leaves a
  // "broken link" visual on a feed, profile grid, lightbox poster, or
  // anywhere else — without each component needing its own onError handler.
  useEffect(() => {
    const isCardLike = (el) => {
      if (!(el instanceof Element)) return false;
      const tid = el.getAttribute('data-testid') || '';
      if (el.matches('[data-broken-hideable]')) return true;
      // Common card test-id conventions used across the app.
      return /(^|-)card($|[-_])|-card-|content-card|community-card|post-card|zenflow-card|grid-item|feed-item/.test(tid);
    };

    const handler = (e) => {
      const t = e.target;
      if (!t) return;
      // Only react to media load failures
      if (!(t instanceof HTMLImageElement) && !(t instanceof HTMLVideoElement)) return;
      // Walk up to the closest card-like container and hide it
      let cur = t;
      while (cur && cur !== document.body) {
        if (isCardLike(cur)) {
          cur.style.display = 'none';
          return;
        }
        cur = cur.parentElement;
      }
      // No card wrapper found — just remove the broken media itself so the
      // empty alt-frame doesn't linger.
      if (t.parentElement) t.style.display = 'none';
    };
    // `error` doesn't bubble on <img>/<video> in the normal phase, so capture.
    window.addEventListener('error', handler, true);
    return () => window.removeEventListener('error', handler, true);
  }, []);

  const handleLogin = (newToken, skipOnboarding = false) => {
    setToken(newToken);
    localStorage.setItem('zenscroll_token', newToken);
    if (skipOnboarding) {
      setHasOnboarded(true);
      localStorage.setItem('zenscroll_onboarded', 'true');
    }
  };

  const handleOnboardingComplete = () => {
    setHasOnboarded(true);
    localStorage.setItem('zenscroll_onboarded', 'true');
  };

  const handleLogout = () => {
    const proceed = () => {
      setToken(null);
      setHasOnboarded(false);
      localStorage.removeItem('zenscroll_token');
      localStorage.removeItem('zenscroll_onboarded');
    };
    window.dispatchEvent(new CustomEvent('zenscroll:mood-survey', { detail: { onDone: proceed } }));
  };

  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter
          token={token}
          setToken={setToken}
          hasOnboarded={hasOnboarded}
          setHasOnboarded={handleOnboardingComplete}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
        <OnboardingTutorial token={token} />
        <HeardAboutSurvey token={token} />
        <RatingSurveyModal token={token} />
        <MobileBottomNav token={token} />
      </BrowserRouter>
      {/* Always-on notch/status-bar mask — content can never render under
          the iPhone notch even while the auto-hiding header is off-screen. */}
      <div className="ios-statusbar-mask" aria-hidden="true" data-testid="ios-statusbar-mask" />
      <Toaster position="top-center" richColors />
      <MoodExitModal />
      {/* Android scroll diagnostic — opens only when `?zsdebug=touch` is on
          the URL.  Captures elementFromPoint at 4 screen positions, root
          scroll ownership, and live touchstart/move/end events with deltaY
          + scrollY so you can read it without USB debugging. */}
      <AndroidScrollDiagnostic />
      <ChromeDebugOverlay />
    </div>
  );
}

export default App;
